import type { SheetCustomModelFieldsFragment } from '@salescore/client-api'
import type { KpiFormMetricFormValue } from '@salescore/client-redux'
import type { CoreModel, GoalConfig, ViewConfigKpi } from '@salescore/core'
import { convertSheetCustomModel } from '@salescore/features'
import { useCallback } from 'react'
import { atom, useSetRecoilState } from 'recoil'

import type { ViewsContext } from '../../components/ViewComponent'
import { type DrillDownParameter, kpiParameterAtom } from '../navigation/atoms'

const prefix = `view`

export interface ViewAbility {
  canCreate: boolean
  canUpdate: boolean
  canDisplayConfigEdit: boolean
  canDelete: boolean
  canCreateNewRecord: boolean
  canOpenRelationInputForm: boolean
  canDownloadCsv: boolean
  canSaveRecord: boolean
  canDeleteRecord: boolean
  // ライセンスのチェックはabilityではないので、ここに含めるべき内容ではないが一旦許す。名前を検討し直したい。
  isVisualizeEnabled: boolean
  isSyncEnabled: boolean
}

export interface SalescoreObjectAbility {
  canSaveSalescoreObject: boolean
  canEditSalescoreObjectRecord: boolean
  canDeleteSalescoreObjectRecord: boolean
  canCreateSalescoreObjectRecord: boolean
  /**
   * 指定したオブジェクトのレコードをシートで編集できるかを返す
   * - シートの「レコードの保存」権限があれば、どのオブジェクトも編集できる
   * - なければ、カスタムオブジェクトの個別権限で編集を許可されたオブジェクトと、そのオブジェクトに追加したカスタム列だけ編集できる
   * - 編集の個別権限はオブジェクトごとに付与されるため、canEditSalescoreObjectRecord ではなくこちらでセルごとに判定する
   * @param modelName 列の書き込み先 (`ViewQueryField.write.streamName`)。書き込み先を持たない列は undefined
   */
  canEditModelRecord: (modelName: string | undefined) => boolean
}

export const viewsContextAtom = atom<ViewsContext>({
  key: `${prefix}/viewsContextAtom`,
  default: {
    models: [],
    views: [],
    viewGroups: [],
    viewsForSider: [],
    onSaveView: async () => {
      await Promise.resolve()
    },
    createView: async (): Promise<undefined> => {
      await Promise.resolve()
    },
    updateView: async (): Promise<undefined> => {
      await Promise.resolve()
    },
    deleteView: async () => {
      await Promise.resolve()
    },
    // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/strict-void-return
    pickView: async () => {
      await Promise.resolve()
    },
    refetchViews: async () => {
      await Promise.resolve()
    },
    onAddResource: async () => {
      // empty
    },
    isSheetMoreActionsCopyVisible: true,
  },
})

export const useSetGoalConfig = (): ((goalConfig: GoalConfig | undefined) => void) => {
  const setKpiParameter = useSetRecoilState(kpiParameterAtom)

  return useCallback(
    (goalConfig: GoalConfig | undefined): void => {
      setKpiParameter((oldValue) => ({
        ...oldValue,
        goalConfig,
      }))
    },
    [setKpiParameter],
  )
}

export const sheetCustomModelsAtom = atom<SheetCustomModelFieldsFragment[]>({
  key: `${prefix}/sheetCustomModelsAtom`,
  default: [],
})

export const autoDrillDownParameterAtom = atom<DrillDownParameter | undefined>({
  key: `${prefix}/autoDrillDownParameterAtom`,
  default: undefined,
})

export const buildConvertedSheetSheetCustomModels = (
  sheetCustomModels: SheetCustomModelFieldsFragment[],
): CoreModel[] => sheetCustomModels.map((x) => convertSheetCustomModel(x))

export const isSavingWithoutInteractionAtom = atom<boolean>({
  key: `${prefix}/isSavingWithoutInteractionAtom`,
  default: false,
})

// XXX: こういうatomをどこに置くのか？設計が破綻している。
export type KpiForm = Partial<Omit<ViewConfigKpi, 'sheet' | 'measure' | 'date' | 'user'>> & {
  name?: string
  viewGroupId?: string | null
  // eslint-disable-next-line sonarjs/todo-tag
  dashboardIds?: string[] // TODO 初期化
  metricFormValue?: KpiFormMetricFormValue
  // 基本的にこのKpiFormの状態=UIの状態になっているが、
  // 絞り込みのUIのみ、他の箇所でも使い回すetcのため、絞り込みコンポーネント内部で独自の状態を持っている。
  // この絞り込みUI以外の箇所から状態を変更した時、コンポーネント内部の状態を更新する必要があるので、
  // これを検出するために以下のキャッシュキーを使っている（詳細はこの呼び出し元であるKpiFormFilterTreeFormItem側を参照）
  filterFormItemCacheKey?: string
}

// eslint-disable-next-line sonarjs/todo-tag
// TODO: useStateに移行できるのではないか
export const kpiFormAtom = atom<KpiForm>({
  key: `${prefix}/kpiForm`,
  default: {},
})

export const kpiFormModifiedAtom = atom<boolean>({
  key: `${prefix}/kpiFormModified`,
  default: false,
})
